"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Plural } from "@lingui/react/macro";
import { getToolName } from "ai";
import { type ReactNode, useCallback, useState } from "react";
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
} from "@/components/ai-elements/chain-of-thought";
import { Response } from "@/components/ai-elements/response";
import { getToolDisplayMeta } from "@/lib/ai/tools/ui/display-meta";
import {
  ActivityLiveStatus,
  ActivityProgressStatus,
  ActivityProgressStatusRow,
} from "./activity-live-status";
import { ActivityReasoning } from "./activity-reasoning";
import { ActivityTimeline } from "./activity-timeline";
import {
  ActivityWorkingTime,
  formatActivityDuration,
} from "./activity-working-time";
import {
  type ActivityStatusKind,
  deriveActivityPresentation,
} from "./derive-activity-presentation";
import type { ActivityDisplayNode } from "./group-activity-items-for-display";
import {
  getParallelToolBatchSummary,
  ParallelToolBatchDisclosure,
} from "./parallel-tool-batch";
import type {
  ActivityRenderGroup,
  ActivityToolDisclosure,
  ActivityToolPresentation,
  MessageViewPhase,
} from "./render-model";
import { useActivityPresentation } from "./use-activity-presentation";

function CurrentActivity({
  batchOpenById,
  focusedBatchId,
  item,
  onBatchFocusRestored,
  onBatchOpenChange,
  onBatchTriggerBlur,
  onBatchTriggerFocus,
  renderTool,
  statusKind,
}: {
  batchOpenById: Readonly<Record<string, boolean>>;
  focusedBatchId: string | null;
  item: ActivityDisplayNode | null;
  onBatchFocusRestored: () => void;
  onBatchOpenChange: (batchId: string, open: boolean) => void;
  onBatchTriggerBlur: (batchId: string) => void;
  onBatchTriggerFocus: (batchId: string) => void;
  renderTool: (
    partIndex: number,
    presentation: ActivityToolPresentation,
  ) => ReactNode;
  statusKind: ActivityStatusKind | null;
}) {
  const { i18n } = useLingui();

  if (statusKind) {
    return <ActivityLiveStatus kind={statusKind} />;
  }
  if (!item) return null;

  if (item.kind === "parallel-tool-batch") {
    const summary = getParallelToolBatchSummary(item, "live", i18n);
    return (
      <div className="min-w-0" data-current-activity="parallel-tool-batch">
        <ActivityProgressStatus
          className="sr-only"
          label={`${summary.title} · ${summary.progress}`}
        />
        <ParallelToolBatchDisclosure
          batch={item}
          key={item.batchId}
          onFocusRestored={onBatchFocusRestored}
          onOpenChange={(open) => onBatchOpenChange(item.batchId, open)}
          onTriggerBlur={() => onBatchTriggerBlur(item.batchId)}
          onTriggerFocus={() => onBatchTriggerFocus(item.batchId)}
          open={batchOpenById[item.batchId] ?? false}
          presentation="live"
          renderTool={renderTool}
          restoreFocus={focusedBatchId === item.batchId}
        />
      </div>
    );
  }

  if (item.kind === "reasoning-group") {
    return (
      <div data-current-activity="reasoning">
        <ActivityReasoning
          isStreaming={item.state !== "done"}
          key={`reasoning-${item.partIndices.join("-")}`}
          presentation="live"
          text={item.text}
        />
      </div>
    );
  }

  if (item.kind === "tool") {
    const isRunning = [
      "input-streaming",
      "input-available",
      "approval-responded",
    ].includes(item.part.state);
    const displayMeta = getToolDisplayMeta(getToolName(item.part));
    const statusLabel =
      displayMeta.effect === "read"
        ? i18n._(msg`Reading ${displayMeta.subject}...`)
        : i18n._(msg`Running ${displayMeta.subject}...`);

    return (
      <div className="min-w-0 space-y-1" data-current-activity="tool">
        {isRunning ? <ActivityProgressStatusRow label={statusLabel} /> : null}
        <div className="min-w-0 py-1">{renderTool(item.partIndex, "live")}</div>
      </div>
    );
  }

  return (
    <Response
      className="max-w-none"
      isAnimating
      key={`interim-${item.partIndex}`}
    >
      {item.part.text}
    </Response>
  );
}

function ActivityHistory({
  batchOpenById,
  durationSeconds,
  final,
  focusedBatchId,
  items,
  onBatchFocusRestored,
  onBatchOpenChange,
  onBatchTriggerBlur,
  onBatchTriggerFocus,
  onOpenChange,
  open,
  phase,
  renderTool,
  toolCount,
}: {
  batchOpenById: Readonly<Record<string, boolean>>;
  durationSeconds?: number;
  final: boolean;
  focusedBatchId: string | null;
  items: ActivityDisplayNode[];
  onBatchFocusRestored: () => void;
  onBatchOpenChange: (batchId: string, open: boolean) => void;
  onBatchTriggerBlur: (batchId: string) => void;
  onBatchTriggerFocus: (batchId: string) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  phase: MessageViewPhase;
  renderTool: (
    partIndex: number,
    presentation: ActivityToolPresentation,
  ) => ReactNode;
  toolCount: number;
}) {
  const { i18n } = useLingui();

  if (items.length === 0) return null;

  const label = final
    ? phase === "incomplete"
      ? i18n._(msg`Activity interrupted`)
      : i18n._(msg`Process details`)
    : i18n._(msg`Previous activity`);

  return (
    <ChainOfThought
      className="w-full max-w-[60rem] space-y-0"
      data-activity-history={final ? "final" : "live"}
      onOpenChange={onOpenChange}
      open={open}
    >
      <div
        className={final ? "border-border/60 border-b pb-2" : undefined}
        data-activity-history-header={final ? "final" : "live"}
      >
        <ChainOfThoughtHeader className="group min-h-7 w-fit max-w-full gap-1 rounded-md py-0.5 pr-1 pl-0 font-normal text-[12px] leading-5 text-muted-foreground hover:bg-muted/35 hover:text-foreground [&>svg:first-child]:hidden [&_svg]:size-3">
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate">{label}</span>
            {final && toolCount > 0 ? (
              <span className="text-muted-foreground text-[12px]">
                ·{" "}
                <Plural
                  comment="Activity summary: number of tool calls"
                  one="# tool"
                  other="# tools"
                  value={toolCount}
                />
              </span>
            ) : null}
            {final && durationSeconds ? (
              <span className="text-muted-foreground text-[12px]">
                · {formatActivityDuration(durationSeconds)}
              </span>
            ) : null}
          </span>
        </ChainOfThoughtHeader>
      </div>
      <ChainOfThoughtContent className="mt-4 mb-1 rounded-lg border border-border/70 bg-card/40 px-2 py-2 shadow-xs">
        <ActivityTimeline
          batchOpenById={batchOpenById}
          focusedBatchId={focusedBatchId}
          items={items}
          onBatchFocusRestored={onBatchFocusRestored}
          onBatchOpenChange={onBatchOpenChange}
          onBatchTriggerBlur={onBatchTriggerBlur}
          onBatchTriggerFocus={onBatchTriggerFocus}
          renderTool={renderTool}
        />
      </ChainOfThoughtContent>
    </ChainOfThought>
  );
}

/** Activity を「完了した履歴」と「現在の一項目」に分けて表示する。 */
export function ActivityGroup({
  group,
  phase,
  hasVisibleAnswer,
  initialStartedAtMs,
  renderTool,
}: {
  group: ActivityRenderGroup;
  phase: MessageViewPhase;
  hasVisibleAnswer: boolean;
  initialStartedAtMs?: number;
  renderTool: (
    partIndex: number,
    presentation: ActivityToolPresentation,
    disclosure?: ActivityToolDisclosure,
  ) => ReactNode;
}) {
  const final = hasVisibleAnswer || phase === "done" || phase === "incomplete";
  const { open, onOpenChange, durationSeconds, workingTiming } =
    useActivityPresentation({
      hasVisibleAnswer,
      initialStartedAtMs,
      phase,
    });
  const [batchOpenById, setBatchOpenById] = useState<Record<string, boolean>>(
    {},
  );
  const [focusedBatchId, setFocusedBatchId] = useState<string | null>(null);
  const [toolOpenByCallId, setToolOpenByCallId] = useState<
    Record<string, boolean>
  >({});
  const [focusedToolCallId, setFocusedToolCallId] = useState<string | null>(
    null,
  );
  const [interactionPhase, setInteractionPhase] = useState(phase);
  const onBatchOpenChange = useCallback(
    (batchId: string, nextOpen: boolean) => {
      setBatchOpenById((current) => ({
        ...current,
        [batchId]: nextOpen,
      }));
      if (nextOpen) onOpenChange(true);
    },
    [onOpenChange],
  );
  const onBatchTriggerFocus = useCallback((batchId: string) => {
    setFocusedBatchId(batchId);
  }, []);
  const onBatchTriggerBlur = useCallback((batchId: string) => {
    setFocusedBatchId((current) => (current === batchId ? null : current));
  }, []);
  const onBatchFocusRestored = useCallback(() => {
    setFocusedBatchId(null);
    setFocusedToolCallId(null);
  }, []);

  if (interactionPhase !== phase) {
    setInteractionPhase(phase);
    if (phase === "done") {
      setBatchOpenById({});
      setFocusedBatchId(null);
      setToolOpenByCallId({});
      setFocusedToolCallId(null);
    }
  }

  const presentation = deriveActivityPresentation({
    group,
    hasVisibleAnswer,
    phase,
  });
  const toolByPartIndex = new Map(
    group.steps.flatMap((step) =>
      step.items.flatMap((item) =>
        item.kind === "tool" ? [[item.partIndex, item] as const] : [],
      ),
    ),
  );
  const renderActivityTool = (
    partIndex: number,
    toolPresentation: ActivityToolPresentation,
  ) => {
    const tool = toolByPartIndex.get(partIndex);
    if (!tool) return renderTool(partIndex, toolPresentation);

    const toolCallId = tool.part.toolCallId;
    return renderTool(partIndex, toolPresentation, {
      open: toolOpenByCallId[toolCallId] ?? tool.part.state === "output-error",
      onOpenChange: (nextOpen) => {
        setToolOpenByCallId((current) => ({
          ...current,
          [toolCallId]: nextOpen,
        }));
      },
      onTriggerBlur: () => {
        setFocusedToolCallId((current) =>
          current === toolCallId ? null : current,
        );
      },
      onTriggerFocus: () => {
        setFocusedToolCallId(toolCallId);
      },
    });
  };
  const displayedBatchOpenById = { ...batchOpenById };
  const displayNodes = presentation.current
    ? [...presentation.completed, presentation.current]
    : presentation.completed;
  let displayedFocusedBatchId = focusedBatchId;
  for (const item of displayNodes) {
    if (item.kind !== "parallel-tool-batch") continue;

    if (!Object.hasOwn(displayedBatchOpenById, item.batchId)) {
      // 単体 Tool が batch へ移っても、利用者が開いていた詳細を閉じない。
      displayedBatchOpenById[item.batchId] = item.tools.some(
        (tool) =>
          toolOpenByCallId[tool.part.toolCallId] ??
          tool.part.state === "output-error",
      );
    }
    if (
      displayedFocusedBatchId === null &&
      item.tools.some((tool) => tool.part.toolCallId === focusedToolCallId)
    ) {
      displayedFocusedBatchId = item.batchId;
    }
  }
  const showWorkingTime =
    phase === "live" && !hasVisibleAnswer && workingTiming !== null;
  const hasVisibleActivity =
    showWorkingTime ||
    presentation.completed.length > 0 ||
    presentation.current !== null ||
    presentation.statusKind !== null;

  if (!hasVisibleActivity) return null;

  return (
    <div className="w-full min-w-0 space-y-2" data-activity-group="">
      {showWorkingTime ? (
        <ActivityWorkingTime
          elapsedBeforeCurrentSegmentMs={
            workingTiming.elapsedBeforeCurrentSegmentMs
          }
          startedAtMs={workingTiming.startedAtMs}
        />
      ) : null}
      <ActivityHistory
        batchOpenById={displayedBatchOpenById}
        durationSeconds={durationSeconds}
        final={final}
        focusedBatchId={displayedFocusedBatchId}
        items={presentation.completed}
        onBatchFocusRestored={onBatchFocusRestored}
        onBatchOpenChange={onBatchOpenChange}
        onBatchTriggerBlur={onBatchTriggerBlur}
        onBatchTriggerFocus={onBatchTriggerFocus}
        onOpenChange={onOpenChange}
        open={open}
        phase={phase}
        renderTool={renderActivityTool}
        toolCount={presentation.toolCount}
      />
      {!final ? (
        <CurrentActivity
          batchOpenById={displayedBatchOpenById}
          focusedBatchId={displayedFocusedBatchId}
          item={presentation.current}
          onBatchFocusRestored={onBatchFocusRestored}
          onBatchOpenChange={onBatchOpenChange}
          onBatchTriggerBlur={onBatchTriggerBlur}
          onBatchTriggerFocus={onBatchTriggerFocus}
          renderTool={renderActivityTool}
          statusKind={presentation.statusKind}
        />
      ) : null}
    </div>
  );
}
